/**
 * Pure selection logic for the PRATUS AI asset-import review card. The card's
 * choices travel back to `create_org_assets` as inline edits (`include_rows`,
 * `exclude_rows`, `update_existing`), which the review queue folds into the
 * resume decision's `edited_args`.
 */
import type { PendingConfirmation, PreviewRow } from './types';

export const ASSET_IMPORT_TOOL = 'create_org_assets';

export type AssetImportStatus =
  | 'new'
  | 'update'
  | 'duplicate'
  | 'needs_review'
  | 'invalid';

export const ASSET_IMPORT_STATUS_LABELS: Record<AssetImportStatus, string> = {
  new: 'New',
  update: 'Update',
  duplicate: 'Duplicate',
  needs_review: 'Needs review',
  invalid: 'Invalid',
};

export const ASSET_IMPORT_STATUSES = Object.keys(
  ASSET_IMPORT_STATUS_LABELS
) as AssetImportStatus[];

export function isAssetImportConfirmation(
  item: PendingConfirmation | undefined
): boolean {
  return (
    item?.tool_name === ASSET_IMPORT_TOOL && Array.isArray(item.preview?.rows)
  );
}

/** Row numbers the tool would save as proposed. */
export function defaultSelection(rows: PreviewRow[]): number[] {
  return rows.filter((r) => r.selectable && r.selected).map((r) => r.index);
}

/** Current selection: the reviewer's edit when present, else the proposal. */
export function currentSelection(
  rows: PreviewRow[],
  edits: Record<string, unknown> | undefined
): number[] {
  const edited = edits?.include_rows;
  if (Array.isArray(edited)) {
    return edited.filter((n): n is number => typeof n === 'number');
  }
  return defaultSelection(rows);
}

export function currentUpdateExisting(
  args: Record<string, unknown>,
  edits: Record<string, unknown> | undefined
): boolean {
  if (typeof edits?.update_existing === 'boolean') return edits.update_existing;
  return args.update_existing !== false;
}

/**
 * Edits for a new selection. `exclude_rows` is cleared so a row the agent
 * excluded can be re-included; unselectable rows are never sent.
 */
export function selectionEdits(
  rows: PreviewRow[],
  selected: number[]
): Record<string, unknown> {
  const selectable = new Set(
    rows.filter((r) => r.selectable).map((r) => r.index)
  );
  const include = [...new Set(selected)]
    .filter((n) => selectable.has(n))
    .sort((a, b) => a - b);
  return { include_rows: include, exclude_rows: [] };
}

export function toggleRow(selected: number[], index: number): number[] {
  return selected.includes(index)
    ? selected.filter((n) => n !== index)
    : [...selected, index];
}

/** Select or clear every selectable row among `visible` (the filtered view). */
export function toggleAllVisible(
  selected: number[],
  visible: PreviewRow[],
  select: boolean
): number[] {
  const ids = visible.filter((r) => r.selectable).map((r) => r.index);
  if (select) return [...new Set([...selected, ...ids])];
  return selected.filter((n) => !ids.includes(n));
}

/** What saving would do with the current choices. */
export function willFor(
  row: PreviewRow,
  selected: number[],
  updateExisting: boolean
): 'create' | 'update' | 'skip' {
  if (!row.selectable || !selected.includes(row.index)) return 'skip';
  if (row.status === 'update') return updateExisting ? 'update' : 'skip';
  return 'create';
}

export function countPlanned(
  rows: PreviewRow[],
  selected: number[],
  updateExisting: boolean
): { create: number; update: number; skip: number } {
  const counts = { create: 0, update: 0, skip: 0 };
  for (const row of rows) counts[willFor(row, selected, updateExisting)] += 1;
  return counts;
}

export function countByStatus(
  rows: PreviewRow[]
): Record<AssetImportStatus, number> {
  const counts: Record<AssetImportStatus, number> = {
    new: 0,
    update: 0,
    duplicate: 0,
    needs_review: 0,
    invalid: 0,
  };
  for (const row of rows) {
    if (row.status in counts) counts[row.status as AssetImportStatus] += 1;
  }
  return counts;
}

export function filterRows(
  rows: PreviewRow[],
  status: AssetImportStatus | 'all'
): PreviewRow[] {
  return status === 'all' ? rows : rows.filter((r) => r.status === status);
}

/** Primary button label in the review modal, e.g. "Save 42 assets". */
export function saveLabel(
  item: PendingConfirmation,
  edits: Record<string, unknown> | undefined
): string {
  const rows = item.preview?.rows ?? [];
  const counts = countPlanned(
    rows,
    currentSelection(rows, edits),
    currentUpdateExisting(item.args, edits)
  );
  const total = counts.create + counts.update;
  return `Save ${total} asset${total === 1 ? '' : 's'}`;
}
